<template>
  <v-dialog v-model="dialog" width="600" persistent>
    <v-card style="border-radius: 10px">
      <!-- Title + Close Button -->
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h6 font-weight-bold">Experience</span>
        <v-btn icon @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Subtitle text -->
        <div class="text-subtitle-2 text-grey mb-4">
          Add your current and past work experiences.
        </div>

        <!-- Top toggle: Fresher / Experienced -->
        <v-row>
          <v-col cols="12">
            <div class="d-flex gap-2 mb-4">
              <button
                v-for="option in jobStatusOptions"
                :key="option"
                type="button"
                :class="[
                  'custom-option-btn',
                  {
                    'custom-option-btn--selected': form.jobStatus === option,
                    'error-border': !form.jobStatus,
                  },
                ]"
                @click="selectJobStatus(option)"
              >
                {{ option }}
              </button>
            </div>
          </v-col>
        </v-row>

        <!-- CONDITIONALLY SHOW ALL FIELDS if user clicked "Experienced" -->
        <v-row v-if="isExperienced">
          <v-col cols="12" sm="6">
            <v-text-field
              label="Job Title *"
              v-model="form.jobTitle"
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              label="Job Category *"
              :items="jobCategories"
              v-model="form.group_id"
              item-title="text"
              item-value="value"
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              label="Industry"
              :items="industries"
              v-model="form.industry_id"
              item-title="text"
              item-value="value"
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              label="Company *"
              v-model="form.company"
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              label="Country *"
              v-model="form.country"
              dense
              outlined
            />
          </v-col>
          <v-col cols="12">
            <v-textarea
              label="Job Description"
              v-model="form.description"
              rows="3"
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              label="Start Date *"
              type="date"
              v-model="form.startDate"
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              label="End Date *"
              type="date"
              v-model="form.endDate"
              :disabled="form.currentlyWorking"
              dense
              outlined
            />
          </v-col>
          <v-col cols="12">
            <v-checkbox
              label="Currently working"
              v-model="form.currentlyWorking"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-if="form.currentlyWorking"
              label="Notice Period"
              :items="noticeOptions"
              v-model="form.noticePeriod"
              dense
              outlined
            />
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Buttons: Clear All / Save -->
      <v-card-actions class="d-flex justify-end">
        <v-btn variant="outlined" @click="clearAll">Clear All</v-btn>
        <v-btn
          variant="flat"
          color="primary"
          :disabled="!canSave"
          @click="saveExperience"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import IndustryService from "@/services/industry.service";

const props = defineProps({
  modelValue: Boolean,
});
const emit = defineEmits(["update:modelValue"]);

const store = useStore();

// Mirror the v-model
const dialog = ref(props.modelValue);
watch(
  () => props.modelValue,
  (val) => {
    dialog.value = val;
  }
);
watch(dialog, (val) => {
  emit("update:modelValue", val);
});

// The top toggle: Fresher / Experienced
const jobStatusOptions = ["Fresher", "Experienced"];
const form = ref({
  jobStatus: "", // either "Fresher" or "Experienced"
  jobTitle: "",
  group_id: null, // job category ID
  industry_id: null,
  company: "",
  country: "",
  description: "",
  startDate: "",
  endDate: "",
  currentlyWorking: false,
  noticePeriod: "",
});

// Computed: is this “Experienced”?
const isExperienced = computed(() => form.value.jobStatus === "Experienced");

// We will need to fetch industries onMounted:
const industries = ref([]);
onMounted(async () => {
  try {
    const res = await IndustryService.index();
    // assume payload is in res.data.data (adjust if your API shape differs)
    const list = res.data.data || [];
    industries.value = list.map((item) => ({
      text: item.name,
      value: item.id,
    }));
  } catch (err) {
    console.error("⛔️ Could not fetch industries:", err);
  }
});

// Get jobCategories from store:
// store.getters["NewCategory/groups"] returns an array of { id, title, ... }
const rawGroups = computed(() => store.getters["NewCategory/groups"] || []);
console.log("🟢 [profile.experience] rawGroups =", rawGroups.value);
const jobCategories = computed(() =>
  rawGroups.value.map((g) => ({ text: g.title, value: g.id }))
);

// Hard‐coded country list (you can adapt if you have a service)
const countryOptions = [
  "Kenya",
  "Uganda",
  "Tanzania",
  "Rwanda",
  "Somalia",
  "Other",
];

// Notice period options:
const noticeOptions = [
  "Available Immediately",
  "Less than 2 Weeks",
  "1 Month",
  "More than 1 Month",
];

// Called when user clicks one of the two buttons
function selectJobStatus(option) {
  form.value.jobStatus = option;
  // If switching back to “Fresher,” clear all fields
  if (option === "Fresher") {
    form.value.jobTitle = "";
    form.value.group_id = null;
    form.value.industry_id = null;
    form.value.company = "";
    form.value.country = "";
    form.value.description = "";
    form.value.startDate = "";
    form.value.endDate = "";
    form.value.currentlyWorking = false;
    form.value.noticePeriod = "";
  }
}

// Clear everything
function clearAll() {
  form.value = {
    jobStatus: "",
    jobTitle: "",
    group_id: null,
    industry_id: null,
    company: "",
    country: "",
    description: "",
    startDate: "",
    endDate: "",
    currentlyWorking: false,
    noticePeriod: "",
  };
}

// Close modal
function closeModal() {
  dialog.value = false;
}

// Computed: only allow Save if jobStatus is chosen, and if “Experienced,” ensure required fields are filled
const canSave = computed(() => {
  if (!form.value.jobStatus) return false;
  if (form.value.jobStatus === "Fresher") {
    return true;
  }
  // If Experienced → check required fields (jobTitle, group_id, company, country, startDate, and if not currentlyWorking then endDate)
  if (
    form.value.jobTitle &&
    form.value.group_id != null &&
    form.value.company &&
    form.value.country &&
    form.value.startDate
  ) {
    if (form.value.currentlyWorking) {
      return true;
    } else {
      return !!form.value.endDate;
    }
  }
  return false;
});

// On Save: commit to vuex and close
function saveExperience() {
  const payload = {
    ...form.value,
  };
  store.dispatch("profile/saveExperience", payload);

  console.log("🟢 [profile.experience] =", store.state.profile.experience);
  dialog.value = false;
  // (Optional) you could fire an alert:
  store.dispatch("alert/success", "Experience saved successfully");

}


watch(
  () => dialog.value,
  (isOpen) => {
    if (isOpen) {
      // If the user has saved at least one experience entry, we prefill from the first element:
      const arr = store.state.profile.experience || [];
      if (arr.length > 0) {
        // const exp = arr[0]; // <-- grab the first saved experience object
        const exp = arr[arr.length - 1];
        form.value.jobStatus = exp.jobStatus || "";
        form.value.jobTitle = exp.jobTitle || "";
        form.value.group_id = exp.group_id || null;
        form.value.industry_id = exp.industry_id || null;
        form.value.company = exp.company || "";
        form.value.country = exp.country || "";
        form.value.description = exp.description || "";
        form.value.startDate = exp.startDate || "";
        form.value.endDate = exp.endDate || "";
        form.value.currentlyWorking = exp.currentlyWorking || false;
        form.value.noticePeriod = exp.noticePeriod || "";
      } else {
        // No experience saved yet, just clear:
        clearAll();
      }
    }
  }
);
</script>

<style scoped>
.custom-option-btn {
  border: 1px solid #c4c4c4;
  background: white;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
}
.custom-option-btn--selected {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
}
.error-border {
  border-color: red !important;
}
.gap-2 {
  gap: 8px;
}
/* Custom Option Button Styles */
.custom-option-btn {
  border-radius: 10px;
  min-width: 120px;
  font-size: 15px;
  font-weight: 500;
  text-transform: none;
  padding: 8px 22px;
  border: 1px solid #bdbdbd;
  background: #fff;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  line-height: 1.2;
  white-space: nowrap;
  outline: none;
  text-decoration: none;
  font-family: inherit;
  box-sizing: border-box;
  min-height: 40px;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* Hover state */
.custom-option-btn:hover {
  background: #f8f9fa;
  border-color: #999;
  color: #555;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Focus state */
.custom-option-btn:focus {
  outline: none;
  outline-offset: 2px;
  border-color: grey;
}

/* Active state */
.custom-option-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Selected state */
.custom-option-btn--selected {
  background: #e3f2fd !important;
  border-color: #0000000c !important;
  color: grey !important;
  font-weight: 600 !important;
  box-shadow: 0 2px 8px rgba(4, 150, 243, 0.2);
}

.custom-option-btn--selected:hover {
  background: #bbdefb !important;
  border-color: grey !important;
  color: grey !important;
}

/* Container styling for option groups */
.visa-status-container,
.education-level-container,
.experience-container {
  width: 100%;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .custom-option-btn {
    min-width: 100px;
    font-size: 14px;
    padding: 6px 16px;
  }
  .gap-10 {
    gap: 8px;
  }
  .details-label {
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .custom-option-btn {
    min-width: 80px;
    font-size: 13px;
    padding: 6px 12px;
  }
  .gap-10 {
    gap: 6px;
  }
  .phone-row {
    flex-direction: column;
    gap: 12px;
  }
}

/* Loading state for buttons (optional) */
.custom-option-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .custom-option-btn {
    border-color: #000;
    color: #000;
  }
  .custom-option-btn--selected {
    background: #000 !important;
    color: #fff !important;
    border-color: #000 !important;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .custom-option-btn,
  .action-btn,
  .upload-card {
    transition: none;
  }
}

/* Print styles */
@media print {
  .custom-option-btn {
    border: 1px solid #000;
    background: transparent;
    color: #000;
  }
  .custom-option-btn--selected {
    background: #ddd !important;
  }
}
</style>
